import React, { useEffect, useState, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Menu, MenuItem, Divider, TablePagination } from "@mui/material";
import Layout from "../../components/layout/Layout";
import Loader from "../../components/loader/Loader";
import { deleteContent, copyTemplate, contentFlagOff } from "../../Redux/Actions/contentAction";
import { getTemplates } from "../../Redux/Actions/templateAction";
import {
  Layers,
  MoreVertical,
  Search,
  Plus,
} from "lucide-react";
import { CopyAllOutlined } from "@mui/icons-material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import toast from "react-hot-toast";
import { canEditCms } from "../../utils/accessControl";
import { useContextUserId } from "../../hooks/useSupportContext";
import "../content/content.css";
import "./layoutlist.css";
import TemplatePreview from "../content/component/TemplatePreview";
import { tr } from "../../i18n";

const ROWS_PER_PAGE_OPTIONS = [8, 12, 20, 50];

function parseCanvaData(canvaInput) {
  try {
    const canvaStr =
      typeof canvaInput === "string"
        ? canvaInput
        : canvaInput != null
          ? JSON.stringify(canvaInput)
          : "{}";
    const data = JSON.parse(canvaStr);
    const bg = data.backgroundColor || data.background || "#ffffff";
    const w = data.canvasWidth || data.width || 1920;
    const h = data.canvasHeight || data.height || 1080;
    const objects = (data.objects || []).map((obj) => ({
      left: obj.left || 0,
      top: obj.top || 0,
      width: (obj.width || 50) * (obj.scaleX || 1),
      height: (obj.height || 50) * (obj.scaleY || 1),
      type: obj.eleType || obj.type || "unknown",
      fill: obj.fill || "transparent",
      stroke: obj.stroke || "",
      strokeWidth: obj.strokeWidth || 0,
      text: obj.text || "",
      fontSize: obj.fontSize || 16,
      url: obj.url || "",
      _name: obj._name || obj.name || "",
      rx: obj.rx || 0,
      clockVariant: obj.clockObj?.variant,
      weatherVariant: obj.weatherObj?.variant,
      weatherCity: obj.weatherObj?.city,
    }));
    return { bg, w, h, objects, count: objects.length };
  } catch {
    return { bg: "#f0f4f8", w: 1920, h: 1080, objects: [], count: 0 };
  }
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now - d;
  if (diff < 60000) return "Just now";
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function renderElement(obj, i, scaleX, scaleY) {
  const x = obj.left * scaleX;
  const y = obj.top * scaleY;
  const ew = Math.max(obj.width * scaleX, 6);
  const eh = Math.max(obj.height * scaleY, 4);
  const cx = x + ew / 2;
  const cy = y + eh / 2;
  const t = obj.type;

  if (t === "text" || t === "textbox") {
    const fs = Math.max(Math.min(obj.fontSize * scaleY, 14), 4);
    const lines = Math.max(1, Math.floor(eh / (fs * 1.3)));
    return (
      <g key={i}>
        {Array.from({ length: lines }).map((_, li) => (
          <rect
            key={li}
            x={x}
            y={y + li * (fs * 1.3)}
            width={li === lines - 1 ? ew * 0.6 : ew}
            height={Math.max(fs * 0.55, 2)}
            rx="1"
            fill={obj.fill !== "transparent" ? obj.fill : "#334155"}
            opacity={0.5}
          />
        ))}
      </g>
    );
  }

  if (t === "scrollstrip") {
    return (
      <g key={i}>
        <rect x={x} y={y} width={ew} height={eh} rx="2" fill="#eef2ff" stroke="#818cf8" strokeWidth="0.5" />
        {[0.15, 0.35, 0.55].map((p, li) => (
          <rect key={li} x={x + ew * 0.08} y={y + eh * p} width={ew * 0.84} height={Math.max(eh * 0.12, 1.5)} rx="0.5" fill="#818cf8" opacity={0.4} />
        ))}
        <polygon points={`${x + ew - 5},${cy - 2} ${x + ew - 5},${cy + 2} ${x + ew - 2},${cy}`} fill="#818cf8" opacity={0.6} />
      </g>
    );
  }

  if (t === "nor-image") {
    return (
      <g key={i}>
        <rect x={x} y={y} width={ew} height={eh} rx="3" fill="#ecfdf5" stroke="#6ee7b7" strokeWidth="0.5" />
        <circle cx={x + ew * 0.3} cy={y + eh * 0.3} r={Math.min(ew, eh) * 0.1} fill="#34d399" opacity={0.7} />
        <polygon
          points={`${x + ew * 0.1},${y + eh * 0.85} ${x + ew * 0.4},${y + eh * 0.45} ${x + ew * 0.6},${y + eh * 0.6} ${x + ew * 0.75},${y + eh * 0.45} ${x + ew * 0.9},${y + eh * 0.65} ${x + ew * 0.9},${y + eh * 0.85}`}
          fill="#34d399"
          opacity={0.45}
        />
      </g>
    );
  }

  if (t === "nor-video") {
    const triSize = Math.min(ew, eh) * 0.22;
    return (
      <g key={i}>
        <rect x={x} y={y} width={ew} height={eh} rx="3" fill="#1e293b" />
        <rect x={x} y={y} width={ew} height={eh} rx="3" fill="url(#videoGrad)" opacity={0.5} />
        <polygon
          points={`${cx - triSize * 0.5},${cy - triSize} ${cx - triSize * 0.5},${cy + triSize} ${cx + triSize},${cy}`}
          fill="#fff"
          opacity={0.85}
        />
      </g>
    );
  }

  if (t === "playlist") {
    const triSize = Math.min(ew, eh) * 0.18;
    return (
      <g key={i}>
        <rect x={x} y={y} width={ew} height={eh} rx="3" fill="#0f172a" />
        <rect x={x + 2} y={y + 2} width={ew - 4} height={eh - 4} rx="2" fill="#1e293b" />
        <polygon
          points={`${cx - triSize * 0.5},${cy - triSize} ${cx - triSize * 0.5},${cy + triSize} ${cx + triSize},${cy}`}
          fill="#06b6d4"
          opacity={0.9}
        />
        <rect x={x + 3} y={y + eh - 5} width={ew * 0.3} height={2} rx="1" fill="#06b6d4" opacity={0.5} />
      </g>
    );
  }

  if (t === "time" || t === "analog-clock") {
    if (t === "analog-clock") {
      const r = Math.min(ew, eh) * 0.4;
      return (
        <g key={i}>
          <circle cx={cx} cy={cy} r={r} fill="#fffbeb" stroke="#f59e0b" strokeWidth="1" />
          <circle cx={cx} cy={cy} r={r * 0.08} fill="#f59e0b" />
          <line x1={cx} y1={cy} x2={cx} y2={cy - r * 0.55} stroke="#f59e0b" strokeWidth="1" strokeLinecap="round" />
          <line x1={cx} y1={cy} x2={cx + r * 0.4} y2={cy} stroke="#f59e0b" strokeWidth="0.8" strokeLinecap="round" />
        </g>
      );
    }
    const fs = Math.max(Math.min(eh * 0.5, 12), 4);
    return (
      <g key={i}>
        <rect x={x} y={y} width={ew} height={eh} rx="2" fill="#fffbeb" opacity={0.6} />
        <text x={cx} y={cy + fs * 0.35} textAnchor="middle" fontSize={fs} fontWeight="700" fill="#d97706" fontFamily="monospace">12:00</text>
      </g>
    );
  }

  if (t === "date") {
    const fs = Math.max(Math.min(eh * 0.4, 10), 3);
    return (
      <g key={i}>
        <rect x={x} y={y} width={ew} height={eh} rx="2" fill="#fdf2f8" opacity={0.6} />
        <rect x={x} y={y} width={ew} height={eh * 0.28} rx="2" fill="#ec4899" opacity={0.25} />
        <text x={cx} y={cy + fs * 0.4} textAnchor="middle" fontSize={fs} fontWeight="700" fill="#be185d" fontFamily="sans-serif">24</text>
      </g>
    );
  }

  if (t === "login" || t === "register") {
    const fs = Math.max(Math.min(eh * 0.35, 11), 4);
    const label = t === "login" ? "Login" : "Register";
    return (
      <g key={i}>
        <rect x={x} y={y} width={ew} height={eh} rx="3" fill="#f0fdf4" stroke="#22c55e" strokeWidth="0.5" opacity={0.9} />
        <text x={cx} y={cy + fs * 0.35} textAnchor="middle" fontSize={fs} fontWeight="600" fill="#15803d" fontFamily="sans-serif">{label}</text>
      </g>
    );
  }

  if (t === "weather-widget") {
    const city = obj.weatherCity || "Weather";
    const emoji = "\u26C5";
    return (
      <g key={i}>
        <defs>
          <linearGradient id={`weatherGrad-${i}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#1e3a5f" />
            <stop offset="100%" stopColor="#2563eb" />
          </linearGradient>
        </defs>
        <rect x={x} y={y} width={ew} height={eh} rx={Math.min(ew, eh) * 0.08} fill={`url(#weatherGrad-${i})`} opacity={0.95} />
        <text x={x + ew * 0.78} y={y + eh * 0.32} fontSize={Math.max(eh * 0.22, 5)} textAnchor="middle">{emoji}</text>
        <text x={x + ew * 0.1} y={y + eh * 0.28} fontSize={Math.max(eh * 0.14, 3)} fill="#7dd3fc" fontFamily="sans-serif">{city}</text>
        <text x={x + ew * 0.1} y={y + eh * 0.58} fontSize={Math.max(eh * 0.28, 4)} fontWeight="700" fill="#fff" fontFamily="sans-serif">12°</text>
      </g>
    );
  }

  if (t === "clock-widget") {
    if (obj.clockVariant === "liquid-crystal") {
      return (
        <g key={i}>
          <rect x={x} y={y} width={ew} height={eh} rx={Math.min(ew, eh) * 0.12} fill="#222222" opacity={0.95} />
          <text
            x={cx}
            y={cy + Math.max(eh * 0.12, 3)}
            textAnchor="middle"
            fontSize={Math.max(Math.min(eh * 0.22, 11), 4)}
            fill="#eeeeee"
            fontFamily="monospace"
          >
            {tr("7. Apr 2026 14:30:45")}
          </text>
        </g>
      );
    }
    if (obj.clockVariant === "neumorphic-analog") {
      const rm = Math.min(ew, eh) * 0.42;
      return (
        <g key={i}>
          <ellipse cx={cx} cy={cy} rx={ew / 2} ry={eh / 2} fill="#e0e5ec" opacity={0.95} />
          <circle cx={cx} cy={cy} r={rm * 0.92} fill="none" stroke="#eef2f7" strokeWidth="1" opacity={0.9} />
          <line x1={cx} y1={cy} x2={cx} y2={cy - rm * 0.35} stroke="#4a5463" strokeWidth="2.5" strokeLinecap="round" />
          <line x1={cx} y1={cy} x2={cx + rm * 0.28} y2={cy - rm * 0.1} stroke="#7a8a9e" strokeWidth="2" strokeLinecap="round" />
          <line x1={cx} y1={cy} x2={cx - rm * 0.05} y2={cy - rm * 0.42} stroke="#e65e5e" strokeWidth="1.2" strokeLinecap="round" />
          <circle cx={cx} cy={cy} r={Math.max(rm * 0.08, 2)} fill="#e0e5ec" />
          <circle cx={cx} cy={cy} r={Math.max(rm * 0.025, 1)} fill="#4a5463" />
        </g>
      );
    }
    const r = Math.min(ew, eh) * 0.08;
    return (
      <g key={i}>
        <defs>
          <linearGradient id={`clockGrad-${i}`} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="rgb(20,30,48)" />
            <stop offset="100%" stopColor="rgb(36,59,85)" />
          </linearGradient>
        </defs>
        <rect x={x} y={y} width={ew} height={eh} rx={Math.min(ew, eh) * 0.06} fill={`url(#clockGrad-${i})`} opacity={0.95} />
        <text x={x + ew * 0.08} y={y + eh * 0.42} fontSize={Math.max(eh * 0.22, 4)} fontWeight="700" fill="#fff" fontFamily="sans-serif">12:00</text>
        <text x={x + ew * 0.62} y={y + eh * 0.38} fontSize={Math.max(eh * 0.08, 3)} fill="#fff" fontFamily="sans-serif">{tr("PM")}</text>
        <text x={x + ew * 0.08} y={y + eh * 0.78} fontSize={Math.max(eh * 0.1, 3)} fill="#fff" opacity={0.9} fontFamily="sans-serif">{tr("Wed · Jun 15")}</text>
        <circle cx={x + ew - r * 1.2} cy={y + r * 1.2} r={r * 0.6} fill="#fff" opacity={0.85} />
      </g>
    );
  }

  if (t === "directory-register") {
    return (
      <g key={i}>
        <rect x={x} y={y} width={ew} height={eh} rx="4" fill="#1e293b" stroke="#475569" strokeWidth="0.5" opacity={0.95} />
        <rect x={x + 4} y={y + 4} width={ew - 8} height={Math.min(eh * 0.12, 14)} rx="2" fill="#475569" opacity={0.4} />
        {[0.22, 0.4, 0.58, 0.76].map((py, idx) => (
          <line key={idx} x1={x + 6} y1={y + eh * py} x2={x + ew - 6} y2={y + eh * py} stroke="#64748b" strokeWidth="0.5" opacity={0.6} />
        ))}
      </g>
    );
  }

  if (t === "qr-code") {
    const s = Math.min(ew, eh);
    const cells = 5;
    const cellSize = s * 0.7 / cells;
    const ox = cx - (cells * cellSize) / 2;
    const oy = cy - (cells * cellSize) / 2;
    const pattern = [
      [1,1,1,0,1],[1,0,0,0,1],[1,0,1,0,1],[0,0,0,0,0],[1,1,1,0,1]
    ];
    return (
      <g key={i}>
        <rect x={x} y={y} width={ew} height={eh} rx="2" fill="#fff" stroke="#e2e8f0" strokeWidth="0.5" />
        {pattern.map((row, ri) =>
          row.map((cell, ci) =>
            cell ? <rect key={`${ri}-${ci}`} x={ox + ci * cellSize} y={oy + ri * cellSize} width={cellSize * 0.9} height={cellSize * 0.9} fill="#1e293b" rx="0.3" /> : null
          )
        )}
      </g>
    );
  }

  if (t === "weather") {
    return (
      <g key={i}>
        <rect x={x} y={y} width={ew} height={eh} rx="3" fill="#f0f9ff" stroke="#7dd3fc" strokeWidth="0.5" />
        <circle cx={cx + ew * 0.08} cy={cy - eh * 0.05} r={Math.min(ew, eh) * 0.18} fill="#fbbf24" opacity={0.8} />
        <ellipse cx={cx - ew * 0.05} cy={cy + eh * 0.1} rx={Math.min(ew, eh) * 0.25} ry={Math.min(ew, eh) * 0.13} fill="#93c5fd" opacity={0.7} />
        <ellipse cx={cx + ew * 0.1} cy={cy + eh * 0.08} rx={Math.min(ew, eh) * 0.18} ry={Math.min(ew, eh) * 0.12} fill="#bfdbfe" opacity={0.8} />
      </g>
    );
  }

  if (t === "temp") {
    const bw = Math.max(ew * 0.15, 3);
    return (
      <g key={i}>
        <rect x={x} y={y} width={ew} height={eh} rx="3" fill="#fef2f2" stroke="#fca5a5" strokeWidth="0.5" />
        <rect x={cx - bw / 2} y={y + eh * 0.15} width={bw} height={eh * 0.55} rx={bw / 2} fill="#fecaca" stroke="#ef4444" strokeWidth="0.5" />
        <circle cx={cx} cy={y + eh * 0.78} r={bw * 0.9} fill="#ef4444" opacity={0.6} />
        <rect x={cx - bw * 0.25} y={y + eh * 0.35} width={bw * 0.5} height={eh * 0.35} fill="#ef4444" opacity={0.5} rx="0.5" />
      </g>
    );
  }

  if (t === "rss") {
    const r = Math.min(ew, eh) * 0.35;
    return (
      <g key={i}>
        <rect x={x} y={y} width={ew} height={eh} rx="3" fill="#fff7ed" stroke="#fdba74" strokeWidth="0.5" />
        <circle cx={cx - r * 0.5} cy={cy + r * 0.5} r={r * 0.2} fill="#f97316" />
        {[0.55, 0.85].map((m, ai) => (
          <path key={ai} d={`M ${cx - r * 0.5} ${cy + r * 0.5} A ${r * m} ${r * m} 0 0 1 ${cx - r * 0.5 + r * m} ${cy + r * 0.5 - r * m}`} fill="none" stroke="#f97316" strokeWidth="1" opacity={0.6 - ai * 0.15} />
        ))}
      </g>
    );
  }

  const fallbackFill = obj.fill && obj.fill !== "transparent" ? obj.fill : "rgba(0,128,128,0.12)";
  return (
    <rect
      key={i}
      x={x} y={y}
      width={ew} height={eh}
      rx={Math.min(obj.rx * scaleX, 4) || 2}
      fill={fallbackFill}
      stroke={obj.stroke || "rgba(0,0,0,0.06)"}
      strokeWidth={Math.min(obj.strokeWidth * scaleX, 1) || 0.5}
      opacity={0.7}
    />
  );
}

function CanvasThumbnail({ canvaStr }) {
  const data = useMemo(() => parseCanvaData(canvaStr), [canvaStr]);
  const { bg, w, h, objects } = data;
  const aspect = w / h;
  const svgW = 280;
  const svgH = svgW / aspect;
  const scaleX = svgW / w;
  const scaleY = svgH / h;

  return (
    <div className="layout-thumb-wrap">
      <svg
        viewBox={`0 0 ${svgW} ${svgH}`}
        className="layout-thumb-svg"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <linearGradient id="videoGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#334155" />
            <stop offset="100%" stopColor="#0f172a" />
          </linearGradient>
          <linearGradient id="emptyIconGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#6366f1" />
            <stop offset="100%" stopColor="#a855f7" />
          </linearGradient>
          <filter id="thumbShadow">
            <feDropShadow dx="0" dy="0.5" stdDeviation="0.8" floodOpacity="0.08" />
          </filter>
        </defs>
        <rect width={svgW} height={svgH} fill={bg} rx="4" />
        {objects.length === 0 ? (
          <g transform={`translate(${svgW / 2}, ${svgH / 2})`}>
            <circle r="52" fill="url(#emptyIconGrad)" opacity="0.12" />
            <g transform="translate(-24, -24) scale(1.7)" stroke="#6366f1" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round">
              <rect x="1" y="1" width="12" height="12" rx="2" />
              <rect x="15" y="1" width="12" height="12" rx="2" />
              <rect x="1" y="15" width="12" height="12" rx="2" />
              <rect x="15" y="15" width="12" height="12" rx="2" />
            </g>
          </g>
        ) : (
          <g filter="url(#thumbShadow)">
            {objects.slice(0, 25).map((obj, i) => renderElement(obj, i, scaleX, scaleY))}
          </g>
        )}
      </svg>
      {data.count > 0 && (
        <span className="layout-thumb-badge">
          <Layers size={10} /> {data.count}
        </span>
      )}
    </div>
  );
}

const LAYOUT_ROWS_PER_PAGE_OPTIONS = [8, 12, 20, 50];

function LayoutList({ user }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const canEdit = canEditCms(user);
  const contextUserId = useContextUserId();
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuItemId, setMenuItemId] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(8);
  const [layoutPageReady, setLayoutPageReady] = useState(false);
  const prevLoadingRef = useRef(false);

  const templates = useSelector((state) => state.Templates.templates);
  const pagination = useSelector((state) => state.Templates.pagination);
  const isTemplatesLoading = useSelector((state) => state.Templates.isTemplatesLoading);
  const isDeleted = useSelector((state) => state.Content.isDeleted);

  // Track when first templates fetch has completed
  useEffect(() => {
    if (isTemplatesLoading) {
      prevLoadingRef.current = true;
    } else {
      if (prevLoadingRef.current) {
        setLayoutPageReady(true);
        prevLoadingRef.current = false;
      }
    }
  }, [isTemplatesLoading]);

  // Debounce search for backend API
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchText);
      setPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchText]);

  // Fetch templates from dedicated endpoint
  useEffect(() => {
    if (!contextUserId) return;
    setLayoutPageReady(false);
    dispatch(
      getTemplates(contextUserId, {
        page: page + 1,
        limit: rowsPerPage,
        search: debouncedSearch || undefined,
      })
    );
  }, [contextUserId, page, rowsPerPage, debouncedSearch, dispatch]);

  // Refetch templates after delete (then reset flag to avoid duplicate calls)
  useEffect(() => {
    if (isDeleted && contextUserId) {
      dispatch(
        getTemplates(contextUserId, {
          page: page + 1,
          limit: rowsPerPage,
          search: debouncedSearch || undefined,
        })
      );
      dispatch(contentFlagOff());
    }
  }, [isDeleted]);

  const templatesList = useMemo(() => templates || [], [templates]);

  const handleChangePage = (e, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleOpenMenu = (e, id) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuItemId(id);
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
    setMenuItemId(null);
  };

  const handleDelete = (id) => {
    dispatch(deleteContent(id));
    handleCloseMenu();
  };

  const handleCopy = (item) => {
    dispatch(copyTemplate(item.id, contextUserId));
    toast.success(tr("Template copied successfully!"));
    handleCloseMenu();
  };

  const handleEdit = (item) => {
    navigate("/design-editor", {
      state: {
        templateId: item.id,
        canva: item.canva,
        name: item.name,
        layoutOwnerUserId: item.user?._id,
        layoutFolderId: item.folderId?._id ?? item.folderId,
      },
    });
  };

  return (
    <Layout title={tr("Layouts")} user={user}>
      <div className="layout-page">
        <div className="layout-page-header">
          <div className="layout-page-title-row">
            <div className="layout-page-title-group">
              <h1 className="layout-page-title">{tr("Layouts")}</h1>
              {(pagination?.totalItems ?? 0) > 0 && (
                <span className="layout-page-count">{pagination.totalItems}</span>
              )}
            </div>
            {canEdit && (
              <button
                className="layout-create-btn"
                onClick={() => navigate("/design-editor")}
              >
                <Plus size={18} />
                {tr("Create Layout")}
              </button>
            )}
          </div>
          <div className="layout-search-bar">
            <Search size={18} className="layout-search-icon" />
            <input
              type="text"
              className="layout-search-input"
              placeholder={tr("Search layouts...")}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            {searchText && (
              <span className="layout-search-result-count">
                {pagination?.totalItems ?? 0} {(pagination?.totalItems ?? 0) !== 1 ? tr("results") : tr("result")}
              </span>
            )}
          </div>
        </div>

        <div className="fl-table-card layout-list-wrap">
          {!layoutPageReady ? (
            <div className="layout-loader-wrap"><Loader /></div>
          ) : (
            <>
          <div className="layout-list-grid">
          {templatesList.length > 0 ? (
            templatesList.map((item) => (
                <div
                  className="content-template-card"
                  key={item.id}
                >
                  <div className="content-template-preview">
                    <TemplatePreview id={item.id} canva={item.canva} />
                    <div className="content-template-badge">
                      <Layers size={10} />
                      <span>{tr("Layout")}</span>
                    </div>
                    {canEdit && (
                      <>
                        <button
                          type="button"
                          className="ct-content-card-menu-btn"
                          onClick={(e) => handleOpenMenu(e, item.id)}
                        >
                          <MoreVertical size={16} />
                        </button>
                        <Menu
                          anchorEl={menuItemId === item.id ? menuAnchor : null}
                          open={menuItemId === item.id && Boolean(menuAnchor)}
                          onClose={handleCloseMenu}
                          onClick={handleCloseMenu}
                          PaperProps={{ sx: { borderRadius: "8px", minWidth: 140, boxShadow: "0 4px 16px rgba(0,0,0,.1)" } }}
                        >
                          <MenuItem onClick={() => handleEdit(item)} sx={{ fontFamily: "'Electrolize', sans-serif", fontSize: "13px", gap: "8px" }}>
                            <EditOutlinedIcon sx={{ fontSize: 18, color: "var(--primary-color)" }} />
                            {tr("Edit")}
                          </MenuItem>
                          <MenuItem onClick={() => handleCopy(item)} sx={{ fontFamily: "'Electrolize', sans-serif", fontSize: "13px", gap: "8px" }}>
                            <CopyAllOutlined sx={{ fontSize: 18, color: "var(--primary-color)" }} />
                            {tr("Duplicate")}
                          </MenuItem>
                          <Divider />
                          <MenuItem onClick={() => handleDelete(item.id)} sx={{ fontFamily: "'Electrolize', sans-serif", fontSize: "13px", gap: "8px", color: "#e53e3e" }}>
                            <DeleteOutlineIcon sx={{ fontSize: 18, color: "#e53e3e" }} />
                            {tr("Delete")}
                          </MenuItem>
                        </Menu>
                      </>
                    )}
                  </div>
                  <div className="content-template-footer">
                    <span className="content-template-name" title={item.name}>
                      {item.name?.substring(0, 24) || tr("Untitled")}
                    </span>
                    <span className="ct-content-card-type">
                      <Layers size={12} />
                      {tr("Layout")}
                    </span>
                  </div>
                </div>
            ))
          ) : (
            <div className="layout-empty">
              <div className="layout-empty-icon">
                <Layers size={52} strokeWidth={1} />
              </div>
              <p className="layout-empty-title">
                {searchText ? tr("No layouts match your search") : tr("No layouts yet")}
              </p>
              <p className="layout-empty-sub">
                {searchText
                  ? tr("Try a different search term")
                  : canEdit
                    ? tr("Create your first layout to get started")
                    : tr("No layouts to display")}
              </p>
              {!searchText && canEdit && (
                <button
                  className="layout-create-btn"
                  onClick={() => navigate("/design-editor")}
                  style={{ marginTop: 8 }}
                >
                  <Plus size={18} />
                  {tr("Create Layout")}
                </button>
              )}
            </div>
          )}
          </div>

          <TablePagination
            rowsPerPageOptions={LAYOUT_ROWS_PER_PAGE_OPTIONS}
            component="div"
            count={pagination?.totalItems ?? 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: "1px solid #f0f0f0",
              fontFamily: "'Electrolize', sans-serif",
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                fontSize: "12px",
                fontFamily: "'Electrolize', sans-serif",
              },
            }}
          />
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default LayoutList;
